import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useAuthStore } from '../../stores/authStore';
import { authService } from '../../services/authService';
import { ApiError } from '../../services/api';

const RECOVERY_FAILED_NOTICE =
  'That login link is no longer valid — links expire after 15 minutes and can only be used once. Request a new one below.';
const OAUTH_FAILED_NOTICE = 'Sign-in did not complete. Please try again.';
const VERIFY_FAILED_NOTICE =
  'That confirmation link is no longer valid. Sign in and request a new one from your account.';

export const AuthCallback: React.FC = () => {
  const [status, setStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [message, setMessage] = useState<string>('Signing you in...');
  const { setSessionToken, setUser, setAuthNotice } = useAuthStore();
  const { verifyAccountRecovery, refreshUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double-execution in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const url = new URL(window.location.href);
    const oauthSessionToken = url.searchParams.get('sessionToken');
    const recoveryToken = url.searchParams.get('recoveryToken');
    const verifyToken = url.searchParams.get('verifyToken');

    (async () => {
      try {
        setAuthNotice(null);
        if (oauthSessionToken) {
          // OAuth: session token provided directly
          setSessionToken(oauthSessionToken);
          const response = await authService.getCurrentUser();
          if (response.user) {
            setUser(response.user);
            setStatus('success');
            setMessage('Login successful. Redirecting...');
          } else {
            setStatus('error');
            setMessage('Login failed: no user information.');
            setAuthNotice({ text: OAUTH_FAILED_NOTICE, kind: 'error' });
          }
        } else if (recoveryToken) {
          // Email recovery: verify token to get session
          await verifyAccountRecovery(recoveryToken);
          setStatus('success');
          setMessage('Login successful. Redirecting...');
        } else if (verifyToken) {
          // Email confirmation: promotes the pending address. Needs no session of its own — the
          // token is the proof — so this also works from a phone that has never signed in.
          const { email } = await authService.verifyEmailToken(verifyToken);
          setStatus('success');
          setMessage('Email confirmed. Redirecting...');
          // Signed in here: refresh so the account widget shows the newly confirmed address.
          if (useAuthStore.getState().sessionToken) {
            await refreshUser();
          }
          setAuthNotice({ text: `${email} is confirmed. You can now use it to sign in.`, kind: 'success' });
        } else {
          setStatus('error');
          setMessage('Missing token in callback URL.');
          setAuthNotice({ text: OAUTH_FAILED_NOTICE, kind: 'error' });
        }
      } catch (err) {
        setStatus('error');
        setMessage('Login failed. Please try again.');
        // This component unmounts as soon as the URL is cleaned below, so the message above is
        // never seen. Hand the reason to the login screen, which is where the player ends up.
        // Prefer the server's wording when it has one — "already in use on another account" is
        // far more actionable than a generic expiry message.
        const serverMessage = err instanceof ApiError ? err.message : undefined;
        let fallback = OAUTH_FAILED_NOTICE;
        if (recoveryToken) fallback = RECOVERY_FAILED_NOTICE;
        else if (verifyToken) fallback = VERIFY_FAILED_NOTICE;
        setAuthNotice({ text: serverMessage || fallback, kind: 'error' });
      } finally {
        // Clean URL back to root to avoid token leakage on refresh
        window.history.replaceState({}, '', '/');
        // Notify app to re-render after URL change
        window.dispatchEvent(new Event('auth-callback-complete'));
      }
    })();
  }, [setSessionToken, setUser, setAuthNotice, verifyAccountRecovery, refreshUser]);

  return (
    <div style={{
      position: 'fixed',
      top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
      background: 'rgba(0,0,0,0.85)', color: 'white', padding: '20px', borderRadius: '8px',
      fontFamily: 'monospace', border: '2px solid #5865F2'
    }}>
      <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>
        {status === 'pending' ? '🔄' : status === 'success' ? '✅' : '⚠️'} Authentication
      </div>
      <div>{message}</div>
    </div>
  );
};
